import { ThemeConfig, MessageConfig } from '../types/theme';

export class ThemeLoader {
  private static instance: ThemeLoader;
  private themeCache: Map<string, ThemeConfig> = new Map();
  private messageCache: Map<string, MessageConfig> = new Map();

  private constructor() {}

  static getInstance(): ThemeLoader {
    if (!ThemeLoader.instance) {
      ThemeLoader.instance = new ThemeLoader();
    }
    return ThemeLoader.instance;
  }

  async loadTheme(clientId: string): Promise<ThemeConfig> {
    // Check cache first
    if (this.themeCache.has(clientId)) {
      return this.themeCache.get(clientId)!;
    }

    try {
      // Try to load client-specific theme
      const clientTheme = await this.loadThemeFile(clientId);
      if (clientTheme) {
        this.themeCache.set(clientId, clientTheme);
        return clientTheme;
      }
    } catch (error) {
      console.warn(`Failed to load theme for client ${clientId}:`, error);
    }

    // Fallback to default theme
    try {
      const defaultTheme = await this.loadThemeFile('default');
      if (defaultTheme) {
        this.themeCache.set(clientId, defaultTheme);
        return defaultTheme;
      }
    } catch (error) {
      console.error('Failed to load default theme:', error);
    }

    // No hardcoded fallback - fail fast
    throw new Error(
      `Theme configuration not found for client "${clientId}" and default theme is missing or invalid. ` +
      `Please ensure /themes/default/config.json exists and is valid.`
    );
  }

  async loadMessages(clientId: string): Promise<MessageConfig> {
    // Check cache first
    if (this.messageCache.has(clientId)) {
      return this.messageCache.get(clientId)!;
    }

    try {
      // Try to load client-specific messages
      const clientMessages = await this.loadMessageFile(clientId);
      if (clientMessages) {
        this.messageCache.set(clientId, clientMessages);
        return clientMessages;
      }
    } catch (error) {
      console.warn(`Failed to load messages for client ${clientId}:`, error);
    }

    // Fallback to default messages
    try {
      const defaultMessages = await this.loadMessageFile('default');
      if (defaultMessages) {
        this.messageCache.set(clientId, defaultMessages);
        return defaultMessages;
      }
    } catch (error) {
      console.error('Failed to load default messages:', error);
    }

    // No hardcoded fallback - fail fast
    throw new Error(
      `Message configuration not found for client "${clientId}" and default messages are missing or invalid. ` +
      `Please ensure /themes/default/messages.json exists and is valid.`
    );
  }

  private async loadThemeFile(clientId: string): Promise<ThemeConfig | null> {
    try {
      const response = await fetch(`/themes/${clientId}/config.json`);
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  private async loadMessageFile(clientId: string): Promise<MessageConfig | null> {
    try {
      const response = await fetch(`/themes/${clientId}/messages.json`);
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  clearCache(): void {
    this.themeCache.clear();
    this.messageCache.clear();
  }
}
